/**
 * WB3-5 — Descriptive AI layer (Inspector top collapsible summary).
 */

import { useEffect, useState } from 'react';
import { Button, Collapse, Spin, Typography } from 'antd';
import type { UniversalBaseEntity } from '@shared/types/entity';
import { useOptionalDerivedWorkbenchFocus } from '../../../hooks/workbench/useOptionalDerivedWorkbenchFocus';
import { useAssistiveAI } from '@frontend/domain-hooks/conscious/useAssistiveAI';
import {
  buildAssistiveGatewayPayload,
  type AssistiveInspectorContext,
} from './build-assistive-payload';
import { css, omitUndefined } from '@shared/utils/omitUndefined';
import styles from './DescriptiveSummarySlot.module.css';

const { Text } = Typography;

export interface DescriptiveSummarySlotProps {
  entity: UniversalBaseEntity;
  enabled?: boolean;
  inspectorContext?: AssistiveInspectorContext;
}

export function DescriptiveSummarySlot({
  entity,
  enabled = true,
  inspectorContext,
}: DescriptiveSummarySlotProps) {
  const derivedFocus = useOptionalDerivedWorkbenchFocus();
  const { loading, error, summary, summarize } = useAssistiveAI(entity.tenantId);
  const [requested, setRequested] = useState(false);

  useEffect(() => {
    if (!enabled || requested) {
      return;
    }
    setRequested(true);
    void summarize(
      buildAssistiveGatewayPayload(
        omitUndefined({
          tenantId: entity.tenantId,
          entity: {
            id: entity.id,
            name: entity.name,
            status: String(entity.status),
            businessDomain: String(entity.businessDomain ?? ''),
          },
          focus: derivedFocus,
          inspectorContext,
          sourceText: 'Summarize current workbench object state for operator.',
        })
      ).sourceText ?? ''
    );
  }, [enabled, entity, derivedFocus, inspectorContext, requested, summarize]);

  if (!enabled) {
    return null;
  }

  return (
    <Collapse
      className={css(styles.root)}
      defaultActiveKey={['descriptive']}
      data-testid="ai-descriptive-summary"
      items={[
        {
          key: 'descriptive',
          label: 'AI 상태 요약',
          children: (
            <div>
              {loading ? <Spin size="small" /> : null}
              {error ? (
                <Text type="danger" data-testid="ai-descriptive-error">
                  {error.message}
                </Text>
              ) : null}
              {summary ? (
                <p className={styles.summary} data-testid="ai-descriptive-text">
                  {summary}
                </p>
              ) : !loading && !error ? (
                <Button
                  type="link"
                  size="small"
                  className={css(styles.toggle)}
                  onClick={() =>
                    void summarize(
                      buildAssistiveGatewayPayload(
                        omitUndefined({
                          tenantId: entity.tenantId,
                          entity: {
                            id: entity.id,
                            name: entity.name,
                            status: String(entity.status),
                            businessDomain: String(entity.businessDomain ?? ''),
                          },
                          focus: derivedFocus,
                          inspectorContext,
                          sourceText: 'Summarize current workbench object state for operator.',
                        })
                      ).sourceText ?? ''
                    )
                  }
                >
                  요약 새로고침
                </Button>
              ) : null}
            </div>
          ),
        },
      ]}
    />
  );
}
